import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowDownTrayIcon, PrinterIcon } from '@heroicons/react/24/outline';
import { Callout, Cluster, ColorSwatch, Container, ExternalLayout, HeaderActions, Skeleton, Stack, TintedText } from '../components/ui';
import { getSharedReport, shareLinksKeys } from '../lib/api/shareLinks.gen';
import { ReportRender } from '../modules/reports';
import { BRAND_FONT_STACKS, type BrandFont } from '../lib/branding';
import '../styles/reportPrint.css';

// The unauthenticated external viewer (P06 §6.1; story 6-1a) —
// `/share/:token`. A TOP-LEVEL `src/pages/` route, not a module route: the
// module host mounts routes from a principal's CAPABILITY DOCUMENT
// (`useModuleRoutes`, gated on an authenticated `/me/capabilities` fetch),
// and there is no principal here at all — the same reason `AcceptInvitePage`/
// `ResetPasswordPage` are plain `src/pages/` routes rather than module ones.
// See `App.tsx`'s own routing block for where this mounts (a bare `<Route>`,
// NOT inside `<PublicLayout>` — that layout carries DefectSnap's own marketing
// header/footer, which is exactly the chrome a shared report must NOT wear;
// "safe organization branding" means the VIEWED ORG's brand, sourced from
// `content.branding` and rendered by `ReportRender` itself, never DefectSnap's).
//
// ── Non-enumeration (P06 §6.1's crux) ───────────────────────────────────────
// Every failure the server can produce — missing, malformed, expired,
// revoked, unauthorized (the org lost its entitlement) — answers with the
// IDENTICAL body (`{ message: 'This link is not available.' }`) and status
// (404). This screen therefore has exactly ONE terminal state, not five: it
// renders `error.message` (via the shared `errorMessage` unwrap, G-27)
// literally BECAUSE the server's message is already the single safe sentence
// — there is no `reason` code on the wire for this screen to branch on, and
// deliberately so (see `contracts/shareLinks.js`'s own header).
//
// ── Distinct, accessible states (P06 §6.1) ──────────────────────────────────
// Loading (skeleton, `aria-busy`), the terminal state (`Callout`, focusable
// heading), print (the SAME `print.css`/`report-print` body-class toggle the
// staff detail screen uses — one stylesheet, one mechanism, not a second
// print pass invented for this screen), and 390px (no bespoke breakpoint
// logic: `ReportRender`'s own fluid Tailwind layout is what the staff screen
// already relies on for the identical clause).
//
// ── Story 2-147wf: this file's real first ExternalLayout consumer ──────────
// `PageLayouts.tsx`'s own `ExternalLayout` doc comment names THIS file's
// still-literal `max-w-3xl` as the exact gap it exists to close — and unlike
// Login/Signup/AcceptInvite (all nested inside `PublicLayout`'s own chrome,
// so a second `py-10` recipe there would double up padding PublicLayout
// already provides), this route is mounted with NO shell at all (App.tsx's
// own routing comment: deliberately outside both `ProtectedRoute` and
// `PublicLayout`) — exactly the "no shell inheritance" case the recipe
// describes. Width is `'operational'` (72rem), not the recipe's own
// `'reading'` default: this is a document viewer (tables, photo grids), the
// same content shape `ReportDetailPage.tsx` (the authenticated staff
// equivalent) already renders at `operational` width via `AppShell`'s own
// canvas — matching that gives the shared view the same measure the staff
// preview uses, a deliberate call made for this story. The success-state
// branding+action row becomes `Cluster` (`justify="between"`, `gap={3}`,
// byte-identical to the former `flex flex-wrap items-center justify-between
// gap-3`), passed through `ExternalLayout`'s `header` slot. The terminal
// (error) state keeps its own `min-h-screen` vertical-centering wrapper —
// no Layout primitive expresses full-viewport centering — but drops the
// literal `max-w-md` for `Container` `width="reading"`.
export function SharedReportPage(): JSX.Element {
  const { token = '' } = useParams<{ token: string }>();

  const query = useQuery({
    queryKey: shareLinksKeys.detail(token),
    queryFn: () => getSharedReport(token),
    enabled: token !== '',
    retry: false,
  });

  useEffect(() => {
    document.body.classList.add('report-print');
    return () => document.body.classList.remove('report-print');
  }, []);

  if (query.isLoading) {
    return (
      <ExternalLayout width="operational" className="px-4 sm:px-6" data-testid="shared-report-loading">
        <div aria-busy="true" aria-label="Loading shared report">
          <Stack gap={4}>
            <Skeleton className="h-10 w-2/3" />
            <Skeleton className="h-64 w-full" />
          </Stack>
        </div>
      </ExternalLayout>
    );
  }

  if (query.isError || !query.data) {
    return (
      <Container width="reading" className="flex min-h-screen items-center px-4">
        <Callout tone="warning" title="This link isn't available" headingLevel={2}>
          This link is missing, expired, revoked, or no longer valid. Ask whoever shared it with you for a fresh
          link.
        </Callout>
      </Container>
    );
  }

  const view = query.data.data;

  return (
    <ExternalLayout
      width="operational"
      className="px-4 sm:px-6"
      header={
        <Cluster justify="between" gap={3} className="report-no-print">
          {(view.content.branding?.logoUrl || view.content.branding?.organizationName) && (
            <Cluster gap={2}>
              {view.content.branding.logoUrl && (
                <img src={view.content.branding.logoUrl} alt="Organization logo" className="h-8 w-8 rounded object-contain" />
              )}
              {view.content.branding.organizationName && (
                <TintedText
                  as="p"
                  className="text-sm font-semibold text-foreground"
                  // Story 6-8: the org's own brand color, server-validated for
                  // WCAG AA against this exact background at save time
                  // (orgBrandingContrast.js) — not a literal, so this is
                  // runtime tenant DATA, not app chrome (P10 §4.1's raw-hex
                  // rule only forbids the latter). Story 2-147xd: moved onto
                  // `TintedText` (components/ui/Chip.tsx) to close a
                  // design-language/no-inline-style finding — same value,
                  // same mechanism, now the primitive layer's concern.
                  color={view.content.branding.primaryColor ?? undefined}
                  // Story 6-9: the org's curated brand font (see
                  // lib/branding.ts's BRAND_FONT_STACKS — same curated set
                  // applied at app boot, reused here verbatim rather than a
                  // second hand-duplicated stack map) — scoped to the header
                  // only, mirroring primaryColor's own scope: the REPORT
                  // BODY's typography stays the template's own
                  // `content.style.font` choice (ReportRender.tsx's
                  // FONT_CLASS), a template-author decision this org-level
                  // override does not reach past.
                  fontFamily={
                    view.content.branding.font ? BRAND_FONT_STACKS[view.content.branding.font as BrandFont] : undefined
                  }
                >
                  {view.content.branding.organizationName}
                </TintedText>
              )}
              {/* Story 6-9 — the "portal accent" role (P10 §1; see
                  contracts/orgBranding.js's header for the full --accent
                  mapping argument). A small brand-accent swatch beside the
                  org name — the same "server-validated tenant DATA, not app
                  chrome" ColorSwatch already renders for OrgBrandingPage's
                  own color-preview dot, aria-hidden by construction
                  (Chip.tsx's own ColorSwatch), so purely decorative here. */}
              {view.content.branding.accentColor && (
                <ColorSwatch className="h-3 w-3" color={view.content.branding.accentColor} />
              )}
            </Cluster>
          )}
          <HeaderActions
            className="ml-auto"
            maxVisible={2}
            actions={[
              ...(view.scope.includes('download')
                ? [
                    {
                      id: 'download',
                      label: 'Download PDF',
                      variant: 'primary' as const,
                      icon: <ArrowDownTrayIcon className="h-4 w-4" />,
                      disabled: !view.downloadUrl,
                      onClick: () => {
                        if (view.downloadUrl) window.open(view.downloadUrl, '_blank', 'noopener');
                      },
                    },
                  ]
                : []),
              { id: 'print', label: 'Print', variant: 'secondary', icon: <PrinterIcon className="h-4 w-4" />, onClick: () => window.print() },
            ]}
          />
        </Cluster>
      }
    >
      {view.scope.includes('download') && !view.downloadUrl && (
        <p className="report-no-print text-xs text-muted-foreground">
          {view.artifact === 'running'
            ? 'The PDF is still being prepared — check back shortly.'
            : 'A downloadable PDF is not available for this report right now.'}
        </p>
      )}
      <ReportRender content={view.content} />
    </ExternalLayout>
  );
}
